
import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { concatMap, reduce } from 'rxjs/operators';
import { CommandController, ControllerMethod, DeterminingStage, ExecutingStage, ForkStage, MethodStage, StageParam } from '@farris/devkit';
import { Command, CommandFork, CommandForkItem, CommandItem, CommandMetadataContent, CommandMethod } from './command-component';
import { ServiceMethod } from './service-component';
import { CommandMetadataService } from './command-metadata.service';
import { HttpClient } from '@angular/common/http';

export class CommandControllerBuilder {

  private commandMetadataService: CommandMetadataService;

  constructor(httpClient: HttpClient) {
    this.commandMetadataService = new CommandMetadataService(httpClient);
  }

  public createCommandControllers(idList: string[]):
    Observable<{ [id: string]: CommandController }> {
    // const result = new Subject<{ [id: string]: CommandController }>();
    const controllers: { [id: string]: CommandController } = {};
    return from(idList).pipe(
      concatMap(id => {
        return this.createCommandController(id);
      }),
      reduce((commandControllers: any, controller: any) => {
        commandControllers[controller.id] = controller;
        return commandControllers;
      }, controllers)
    );
  }

  private createCommandController(id: string): Observable<CommandController> {
    return this.commandMetadataService.getCommandMetadataContent(id)
      .pipe(
        concatMap(commandMetedataContent => {
          return this.buildCommandControllerFromMetadata(commandMetedataContent);
        })
      );
    // const controllerUri = `./assets/${id}.json`;
    // return this.httpClient.get(controllerUri)
    //   .pipe(
    //     concatMap((data: any) => {
    //       const metadataContent = data.Content as CommandComponent;
    //       return this.buildCommandControllerFromMetadata(metadataContent);
    //     })
    //   );
  }

  private buildCommandControllerFromMetadata(metadataContent: CommandMetadataContent): Observable<CommandController> {
    return from(metadataContent.Commands)
      .pipe(
        concatMap((command: Command) => {
          return this.buildControllerMethodFromCommand(command);
        }),
        reduce((methods: any, methodObject: any) => {
          methods[methodObject.code] = methodObject;
          return methods;
        }, {}),
        concatMap(methods => {
          const commandController: CommandController = {
            id: metadataContent.Id,
            code: metadataContent.Code,
            name: metadataContent.Name,
            methods
          };
          return of(commandController);
        })
      );
  }

  private buildControllerMethodFromCommand(command: Command): Observable<ControllerMethod> {
    return from(command.Items)
      .pipe(
        concatMap((commandItem: CommandItem) => {
          if (+commandItem.Type === 0) {
            return this.buildExecutingStageFromCommand(commandItem);
          } else if (+commandItem.Type === 2) {
            return this.buildForkStageFromCommandItem(commandItem);
          } else {
            throw new Error(`Unknow command item type ${commandItem.Type} .`);
          }
        }),
        reduce((stages: MethodStage[], value: any) => {
          stages.push(value);
          return stages;
        }, []),
        concatMap((stages: MethodStage[]) => {
          const params = command.Parameters ? command.Parameters.map(commandParam => {
            return {
              name: commandParam.Code,
              type: commandParam.ParameterType,
              expression: ''
            };
          }) : [];
          const controlMethod: ControllerMethod = {
            id: command.Id,
            code: command.Code,
            name: command.Name,
            params,
            stages
          };
          return of(controlMethod);
        })
      );
  }

  private buildForkStageFromCommandItem(commandItem: CommandItem): Observable<MethodStage> {
    const itemContent = commandItem.Content as CommandFork;
    const forkItems = itemContent.Items;
    return from(forkItems).pipe(
      concatMap(forkItem => {
        return this.buildDeterminingStageFromForkItem(forkItem);
      }),
      reduce((stages: DeterminingStage[], stage: any) => {
        stages.push(stage);
        return stages;
      }, []),
      concatMap((stages: DeterminingStage[]) => {
        const forkStage: ForkStage = {
          type: 'fork',
          name: itemContent.Code,
          stages
        };
        return of(forkStage);
      })
    );
  }

  private buildDeterminingStageFromForkItem(forkItem: CommandForkItem): Observable<DeterminingStage> {
    const commandItems = forkItem.Items;
    return this.buildExecutingStagesFromCommandItems(commandItems)
      .pipe(
        concatMap((stages: ExecutingStage[]) => {
          const determiningStage: DeterminingStage = {
            type: 'determing',
            name: forkItem.Code,
            conditonType: forkItem.ConditionType,
            condition: forkItem.Express,
            stages
          };
          return of(determiningStage);
        })
      );
  }

  private buildExecutingStagesFromCommandItems(commandItems: CommandItem[]): Observable<ExecutingStage[]> {
    return from(commandItems)
      .pipe(
        concatMap((commandItem: CommandItem) => {
          return this.buildExecutingStageFromCommand(commandItem);
        }),
        reduce((stages: ExecutingStage[], value: any) => {
          stages.push(value);
          return stages;
        }, [])
      );
  }

  private buildExecutingStageFromCommand(commandItem: CommandItem): Observable<MethodStage> {
    const itemContent = commandItem.Content as CommandMethod;
    return this.commandMetadataService.getServiceMetadataContent(itemContent.ComponentCode)
      .pipe(
        concatMap(serviceComponent => {
          const params = itemContent.ParamConfigs ?
            itemContent.ParamConfigs.map<StageParam>((paramConfig) => {
              return { name: paramConfig.ParamCode, expression: paramConfig.ParamExpress };
            }) : [];
          const serviceComponentMethod = serviceComponent.Operations
            .find((method: ServiceMethod) => method.Id === itemContent.MethodId);
          if (!serviceComponentMethod) {
            throw new Error(`在服务构件'${serviceComponent.Name}'中，未找到${itemContent.Name}方法`);
          }
          const methodStage: ExecutingStage = {
            type: 'executing',
            name: serviceComponentMethod.Name,
            source: serviceComponent.Source,
            service: serviceComponent.ClassName,
            method: serviceComponentMethod.Code,
            params
          };
          return of(methodStage);
        })
      );
  }
}
